import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core/dist/esm/es6/rendering/cellRenderers/iCellRenderer';
import { ChangeDetectorRef, Component, HostBinding } from '@angular/core';
import isFunction from 'lodash-es/isFunction';

interface Button {
    clickAction: (data: any) => void;
    icon: string;
    tooltip?: string;
    text?: string;
    disabled?: boolean;
    visible?: boolean;
    computeDisabledState?: (data: any) => boolean;
    computeVisibleState?: (data: any) => boolean;
    computeTooltip?: (data: any) => string;
}

interface ActionButtonsRendererParams {
    buttons: Button[];
    alignment?: 'left' | 'center' | 'right';
    invisibleButtonPlaceholder?: boolean;
}

@Component({
    selector: 'app-action-buttons',
    templateUrl: './action-buttons-renderer.component.html',
    styleUrls: ['./action-buttons-renderer.component.scss'],
    standalone: false,
})
export class ActionButtonsRendererComponent implements ICellRendererAngularComp {
    public buttons: Button[] = [];
    public computedFields: { [key: string]: any }[] = [];
    protected alignment?: 'left' | 'center' | 'right';
    protected invisibleButtonPlaceholder: boolean = false;
    private rowData: any;

    private computableFieldMap: [keyof Button, keyof Button][] = [
        ['disabled', 'computeDisabledState'],
        ['visible', 'computeVisibleState'],
        ['tooltip', 'computeTooltip'],
    ];

    @HostBinding('class') get hostClass() {
        return this.alignment;
    }

    constructor(private cdr: ChangeDetectorRef) {}

    agInit(params: ICellRendererParams): void {
        this.updateComponentState(params);
    }

    refresh(params: ICellRendererParams): boolean {
        this.updateComponentState(params);
        return true;
    }

    private updateComponentState(params: ICellRendererParams): void {
        const rawParams = params.colDef?.cellRendererParams;
        const cellRendererParams = isFunction(rawParams) ? rawParams(params) : rawParams || {};

        this.buttons = cellRendererParams.buttons || [];
        this.computedFields = [];
        this.alignment = cellRendererParams.alignment;
        this.invisibleButtonPlaceholder = cellRendererParams.invisibleButtonPlaceholder || false;
        this.rowData = params.data;

        this.buttons.forEach((button, index) => {
            const computedValues: { [key: string]: any } = {};

            this.computableFieldMap.forEach(([field, computeFnKey]) => {
                const computeFn = button[computeFnKey];
                if (isFunction(computeFn)) {
                    computedValues[field] = computeFn(this.rowData);
                }
            });

            this.computedFields.push(computedValues);
        });

        // Trigger change detection if needed
        this.cdr.markForCheck();
    }

    public btnClickHandler(index: number): void {
        const button = this.buttons[index];
        button.clickAction(this.rowData);
    }
}
